"use client";

import type React from "react";

import { useState } from "react";
import { Check, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { cn } from "@/lib/utils";
import {
  MarketingEmailsInput,
  MarketingEmailsReason,
} from "@/interfaces/marketing-emails";
import { MarketingEmailsService } from "@/services/MarketingEmailsService";
import { useNavigate } from "react-router-dom";

export default function LeadGenerationForm() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [reason, setReason] = useState<MarketingEmailsReason | "">("");
  const [description, setDescription] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validação do nome
    if (!name.trim()) {
      setError("Por favor, insira seu nome");
      return;
    }

    // Validação do email
    if (!email || !/^\S+@\S+\.\S+$/.test(email)) {
      setError("Por favor, insira um endereço de email válido");
      return;
    }

    // Validação da fonte
    if (!reason) {
      setError("Por favor, selecione como você nos encontrou");
      return;
    }

    // Validação da descrição para OTHER
    if (reason === MarketingEmailsReason.OTHER && !description) {
      setError("Por favor, especifique como você nos encontrou");
      return;
    }

    setIsSubmitting(true);

    try {
      // Instanciando o serviço
      const service = new MarketingEmailsService();

      // Preparando o payload
      const payload: MarketingEmailsInput = {
        name: name,
        email: email,
        reason: reason as MarketingEmailsReason,
        description:
          reason === MarketingEmailsReason.OTHER ? description : undefined,
      };

      // Enviando os dados para o serviço
      await service.create(payload);

      // Simulando um pequeno delay para feedback visual
      setTimeout(() => {
        setIsSubmitting(false);
        setIsSubmitted(true);
      }, 1000);
    } catch (err) {
      console.error("Erro ao enviar formulário:", err);
      setError(
        "Ocorreu um erro ao enviar o formulário. Por favor, tente novamente."
      );
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen w-full bg-[#D6D8CB] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-lg shadow-xl p-8 text-center">
          <div className="rounded-full bg-[#EE7B11] p-3 mb-4 inline-flex">
            <Check className="h-8 w-8 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-[#050505] mb-2">
            Obrigado, {name}!
          </h2>
          <p className="text-[#050505] mb-6">
            Seu e-book "Tendências de Páscoa 2025: Os Sabores Mais Desejados do
            Ano" está a caminho da sua caixa de entrada.
          </p>
          <div className="mb-6">
            <img
              src="/campanha_ebook.jpg"
              alt="Capa do E-book"
              // className="w-[150px] h-[200px] rounded-md shadow-lg border-2 border-[#EE7B11] mx-auto object-cover"
              loading="lazy"
            />
          </div>
          <Button
            className="bg-[#EE7B11] hover:bg-[#EE7B11]/90 text-white border-none"
            onClick={() => navigate("/")}
          >
            Ir para o site
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#D6D8CB] flex flex-col md:flex-row items-stretch">
      <div className="w-full md:w-1/2 bg-white/50 p-8 flex flex-col items-center justify-center">
        <div className="max-w-md w-full">
          <div className="relative mb-8">
            <div className="absolute -inset-2 bg-[#EE7B11]/20 rounded-lg blur-sm"></div>
            <div className="relative">
              <img
                src="/campanha_ebook.jpg"
                alt="Capa do E-book"                
                loading="lazy"
              />
            </div>
          </div>
          <h3 className="font-semibold text-[#050505] text-2xl text-center mb-4">
            Tendências de Páscoa 2025
          </h3>
          <p className="text-[#050505]/80 text-center text-lg mb-6">
            Os Sabores Mais Desejados do Ano
          </p>
          <div className="flex items-center justify-center text-[#EE7B11]">
            <BookOpen className="h-6 w-6 mr-2" />
            <span className="text-lg font-medium">
              10 páginas de conteúdo exclusivo
            </span>
          </div>
        </div>
      </div>

      {/* Form Section */}
      <div className="w-full md:w-1/2 bg-white p-8 flex items-center justify-center">
        <div className="max-w-md w-full">
          <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#050505] mb-2">
              E-book Gratuito
            </h2>
            <p className="text-[#050505]/80 text-lg">
              Obtenha sua cópia do nosso guia exclusivo
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Campo de Nome */}
            <div className="space-y-2">
              <Label
                htmlFor="name"
                className="text-[#050505] font-medium text-lg"
              >
                Nome completo
              </Label>
              <Input
                id="name"
                type="text"
                placeholder="Seu nome"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="bg-[#D6D8CB]/30 border-[#EE7B11]/20 focus:border-[#EE7B11] focus:ring-[#EE7B11] text-lg py-6"
              />
            </div>

            <div className="space-y-2">
              <Label
                htmlFor="email"
                className="text-[#050505] font-medium text-lg"
              >
                Endereço de email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-[#D6D8CB]/30 border-[#EE7B11]/20 focus:border-[#EE7B11] focus:ring-[#EE7B11] text-lg py-6"
              />
            </div>

            <div className="space-y-3">
              <Label className="text-[#050505] font-medium text-lg">
                Como você nos encontrou?
              </Label>
              <RadioGroup
                value={reason}
                onValueChange={(value) =>
                  setReason(value as MarketingEmailsReason | "")
                }
                className="space-y-3"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    id="tiktok"
                    value={MarketingEmailsReason.TIKTOK}
                    className="border-[#EE7B11]"
                  />
                  <Label htmlFor="tiktok" className="text-[#050505] text-lg">
                    TikTok
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    id="facebook"
                    value={MarketingEmailsReason.FACEBOOK}
                    className="border-[#EE7B11]"
                  />
                  <Label htmlFor="facebook" className="text-[#050505] text-lg">
                    Facebook
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    id="instagram"
                    value={MarketingEmailsReason.INSTAGRAM}
                    className="border-[#EE7B11]"
                  />
                  <Label htmlFor="instagram" className="text-[#050505] text-lg">
                    Instagram
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem
                    id="other"
                    value={MarketingEmailsReason.OTHER}
                    className="border-[#EE7B11]"
                  />
                  <Label htmlFor="other" className="text-[#050505] text-lg">
                    Outro
                  </Label>
                </div>
              </RadioGroup>

              {reason === MarketingEmailsReason.OTHER && (
                <div className="space-y-2">
                  <Label
                    htmlFor="description"
                    className="text-[#050505] font-medium text-lg"
                  >
                    Por favor, especifique
                  </Label>
                  <Input
                    id="description"
                    placeholder="Como você nos encontrou?"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="bg-[#D6D8CB]/30 border-[#EE7B11]/20 focus:border-[#EE7B11] focus:ring-[#EE7B11] text-lg py-6"
                  />
                </div>
              )}
            </div>

            {error && (
              <div className="text-red-500 text-lg font-medium">{error}</div>
            )}

            <Button
              type="submit"
              className={cn(
                "w-full bg-[#EE7B11] hover:bg-[#EE7B11]/90 text-white font-medium text-lg py-6",
                isSubmitting && "opacity-70 cursor-not-allowed"
              )}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Enviando..." : "Obtenha Seu E-book Gratuito"}
            </Button>

            <p className="text-sm text-center text-[#050505]/60 pt-4">
              Ao enviar, você concorda em receber emails de marketing da pedidoz
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
